import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, resetUserState, getAllUsers, selectUsersLoading, selectUsersSuccess, selectUsersError, selectUsersMessage } from '../../features/users/userSlice';
import { useToast } from '../../contexts/ToastContext';
import UserForm from './UserForm';
import LoadingSpinner from '../ui/LoadingSpinner';

function EditUser({ user, onCancel, onSuccess }) {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectUsersLoading);
  const isSuccess = useSelector(selectUsersSuccess);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const toast = useToast();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Reset state when component mounts
  useEffect(() => {
    dispatch(resetUserState());
    setHasSubmitted(false);
  }, [dispatch]);
  
  useEffect(() => {
    // Only handle success/error if we actually submitted a form
    if (hasSubmitted) {
      if (isSuccess) {
        toast.showSuccess('User updated successfully');
        dispatch(resetUserState());
        setHasSubmitted(false);
        // Refresh the users list in the background
        dispatch(getAllUsers());
        onSuccess();
      }
      
      if (isError && message) {
        toast.showError(message);
        setHasSubmitted(false); // Reset so user can try again
      }
    }
  }, [isSuccess, isError, message, dispatch, toast, onSuccess, hasSubmitted]);
  
  // Handle form submission
  const handleSubmit = (userData) => {
    setHasSubmitted(true);
    dispatch(updateUser({ userId: user.id, userData }));
  };
  
  // Handle cancel - reset state and navigate back
  const handleCancel = () => {
    dispatch(resetUserState());
    setHasSubmitted(false);
    onCancel();
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <p className="text-sm text-gray-600">Update details for {user.username}</p>
        </div>
        <button 
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          onClick={handleCancel}
          disabled={isLoading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to User List
        </button>
      </div>
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="flex flex-col items-center">
            <LoadingSpinner />
            <p className="mt-2 text-sm text-gray-600">Updating user...</p>
          </div>
        </div>
      )}
      
      {/* Form Body */}
      <div className="p-6">
        <UserForm 
          user={user}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

export default EditUser;
